import { PureComponent, ReactElement } from 'react';
import { Button, Dropdown, FormInstance, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Parameter, ParameterList, Variable, VariableList } from '@methodset/calculator-ts';
import { Globals } from 'constants/Globals';
import { Justify } from 'components/Justify/Justify';
import { Configuration, ConfigurationSpec, ConfigurationType } from '@methodset/endpoint-client-ts';
import { ConfigurationUtils } from 'utils/ConfigurationUtils';
import { FormDivider } from 'components/FormDivider/FormDivider';
import { ConfigurationEditor } from '../../ConfigurationEditor';
import classNames from 'classnames';
import update from 'immutability-helper';
import './OverridesConfiguration.less';

// Map of variable id to variable for overridables.
type VariableMap = { [key: string]: Variable };
// Set of variable ids that can be overridden.
type OverrideSet = Set<string>;

export type ChangeCallback = (configuration: Configuration) => void;

export type OverridesConfigurationProps = typeof OverridesConfiguration.defaultProps & {
    formRef: React.RefObject<FormInstance>,
    className?: string,
    variableSpecs?: ConfigurationSpec[],
    configuration?: Configuration,
    variables: VariableList,
    onChange: ChangeCallback
}

export type OverridesConfigurationState = {
    configurationSpecs: ConfigurationSpec[]
}

export class OverridesConfiguration extends PureComponent<OverridesConfigurationProps, OverridesConfigurationState> {

    static defaultProps = {
        variableSpecs: [] as ConfigurationSpec[],
        configuration: {} as Configuration
    }

    private overrideSet: OverrideSet;
    private variableMap: VariableMap;

    constructor(props: OverridesConfigurationProps) {
        super(props);
        this.overrideSet = this.buildOverrideSet();
        this.variableMap = this.buildVariableMap();
        this.state = {
            configurationSpecs: this.buildConfigurationSpecs()
        };
        this.handleConfigurationAdd = this.handleConfigurationAdd.bind(this);
        this.handleConfigurationRemove = this.handleConfigurationRemove.bind(this);
        this.handleConfigurationChange = this.handleConfigurationChange.bind(this);
    }

    private handleConfigurationAdd(variable: Variable): void {
        this.overrideSet.add(variable.id);
        const configurationSpecs = this.buildConfigurationSpecs();
        this.setState({ configurationSpecs: configurationSpecs });
    }

    private handleConfigurationRemove(spec: ConfigurationSpec): void {
        this.overrideSet.delete(spec.key);
        const configurationSpecs = this.buildConfigurationSpecs();
        this.setState({ configurationSpecs: configurationSpecs });
        const configuration = update(this.props.configuration, {
            $unset: [spec.key]
        });
        this.props.onChange(configuration);
    }

    private handleConfigurationChange(configuration: Configuration): void {
        this.props.onChange(configuration);
    }

    private buildOverrideSet(): Set<string> {
        const overrides = new Set<string>();
        for (const key of Object.keys(this.props.configuration)) {
            overrides.add(key);
        }
        return overrides;
    }

    private buildVariableMap(): VariableMap {
        const parameterMap: VariableMap = {};
        this.props.variables.forEach((variable: Variable) => {
            parameterMap[variable.id] = variable;
        });
        return parameterMap;
    }

    private buildConfigurationSpecs(): ConfigurationSpec[] {
        const configurationSpecs: ConfigurationSpec[] = [];
        this.props.variables.forEach((variable: Variable) => {
            if (this.overrideSet.has(variable.id)) {
                const configurationSpec = ConfigurationUtils.createConfigurationSpec(
                    ConfigurationType.TEXT,
                    variable.id,
                    variable.name,
                    variable.description
                );
                configurationSpecs.push(configurationSpec!);
            }
        });
        return configurationSpecs;
    }

    public render(): ReactElement {
        return (
            <div className={classNames('x-overridesconfiguration', this.props.className)}>
                {this.state.configurationSpecs.length > 0 &&
                    <FormDivider
                        {...Globals.FORM_LAYOUT}
                        formRef={this.props.formRef}
                        label="Variable Overrides"
                        bold={true}
                        colon={false}
                        inline={true}
                    />
                }
                {this.state.configurationSpecs.length > 0 &&
                    <ConfigurationEditor
                        formRef={this.props.formRef}
                        configuration={this.props.configuration}
                        configurationSpecs={this.state.configurationSpecs}
                        variableSpecs={this.props.variableSpecs}
                        authentications={[]}
                        showEmpty={true}
                        showExpressions={true}
                        onDelete={this.handleConfigurationRemove}
                        onChange={this.handleConfigurationChange}
                    />
                }
                <Justify justification="right">
                    <Dropdown
                        trigger={['click']}
                        //disabled={this.props.parameterSpecs.length === 0}
                        disabled={Object.keys(this.variableMap).length === 0}
                        overlay={(
                            <Menu>
                                {Object.values(this.variableMap).map(variable => (
                                    <Menu.Item
                                        key={variable.id}
                                        disabled={!!this.overrideSet.has(variable.id)}
                                        onClick={() => this.handleConfigurationAdd(variable)}
                                    >
                                        {this.variableMap[variable.id].name}
                                    </Menu.Item>
                                ))}
                            </Menu>
                        )}
                    >
                        <Button>
                            Override Parameter <DownOutlined />
                        </Button>
                    </Dropdown>
                </Justify>
            </div>
        );
    }

}
